// Module chrome (BLD-26): the authenticated top bar and a light sub-navigation; independent of PlanShell.
import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { ClipboardCheck, Layers } from "lucide-react";
import { SkipLink, TopBar } from "../../components/shell.tsx";
import { Credits } from "../../components/brand.tsx";
import { EmptyState, ErrorState, Skeleton, SelectField, cx } from "../../components/ui.tsx";
import { GuardedLink } from "../../components/plan-shell.tsx";
import { useGuardedNavigate } from "../../lib/guard.tsx";
import { useInstitutionParam, useMaturityInstitutions, withInstitution, type MaturityInstitution } from "../../lib/maturity.ts";

export function MaturityLayout({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <SkipLink />
      <TopBar />
      <main id="conteudo" className={cx("mx-auto w-full flex-1 px-4 py-6 sm:px-7", wide ? "max-w-[1180px]" : "max-w-5xl")}>{children}</main>
      <Credits className="px-4 pb-6" />
    </div>
  );
}

/**
 * Resolves the institution for module pages: `?instituicao=` when it is one of the caller's, otherwise the first
 * eligible one. Renders the instructor-only explanation when there is none.
 */
export function WithInstitution({ children }: { children: (institution: MaturityInstitution, all: MaturityInstitution[]) => ReactNode }) {
  const q = useMaturityInstitutions();
  const wanted = useInstitutionParam();
  if (q.isPending) return <MaturityLayout><Skeleton lines={5} /></MaturityLayout>;
  if (q.isError) return <MaturityLayout><ErrorState error={q.error} onRetry={() => void q.refetch()} /></MaturityLayout>;
  const list = q.data;
  if (!list.length) {
    return (
      <MaturityLayout>
        <EmptyState title="Diagnóstico institucional disponível apenas para instrutores.">
          O módulo é usado pelo instrutor para registrar o diagnóstico de consenso da instituição. Se você é instrutor, peça para ser incluído como instrutor em uma oficina da instituição.
        </EmptyState>
      </MaturityLayout>
    );
  }
  const current = list.find((i) => i.id === wanted) ?? list[0]!;
  return <>{children(current, list)}</>;
}

const TABS = [
  { href: "/diagnosticos", label: "Avaliações", Icon: ClipboardCheck },
  { href: "/diagnosticos/modelos", label: "Modelos de questionário", Icon: Layers },
];

/** Institution selector (only when there is more than one) and the Avaliações · Modelos tabs. */
export function ModuleNav({ institution, all }: { institution: MaturityInstitution; all: MaturityInstitution[] }) {
  const [location] = useLocation();
  const go = useGuardedNavigate();
  const base = location.startsWith("/diagnosticos/modelos") ? "/diagnosticos/modelos" : "/diagnosticos";
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-line">
      <nav aria-label="Diagnóstico institucional" className="-mb-px flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const active = t.href === base;
          return (
            <GuardedLink key={t.href} href={withInstitution(t.href, institution.id)} aria-current={active ? "page" : undefined}
              className={cx("inline-flex min-h-11 items-center gap-2 border-b-2 px-3 text-sm whitespace-nowrap transition-colors duration-[var(--duration-base)]",
                active ? "border-accent font-semibold text-primary" : "border-transparent text-muted hover:text-ink")}>
              <t.Icon className="size-4" aria-hidden />{t.label}
            </GuardedLink>
          );
        })}
      </nav>
      {all.length > 1 && (
        <div className="w-full sm:w-72 [&>div]:my-1">
          <SelectField label="Instituição" value={institution.id} onChange={(e) => go(withInstitution(base, e.target.value))}>
            {all.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </SelectField>
        </div>
      )}
    </div>
  );
}
